import { useMemo } from 'react'
import { useTranslation } from 'react-i18next'

import {
  CardOrderCancellationReason,
  CardOrderDeliveyDate,
  CardOrderInformation,
  CardOrderPaymentDate,
  CardOrderProfile,
  CardOrderStatuses,
  CardOrderSummaryValues,
} from '@/components'
import { Alert, CloseIcon, WarningIcon } from '@/lib'

import { TabGeneralProps } from './tab-general.type'
import { ProfileTaxStatusEnum } from '@/types'
import styles from './tab-general.module.css'

export const TabGeneral = ({ isLoading, saleOrder }: TabGeneralProps) => {
  const { t } = useTranslation('screenOrderCenterPage')

  const ProfileWarning = useMemo(() => {
    const profileStatus = saleOrder?.buyer?.taxStatus

    if (profileStatus === ProfileTaxStatusEnum.pending) {
      return (
        <Alert
          leftIcon={<WarningIcon />}
          variant="warning"
          title={t('orderCenterShowSubtitleProfileIncompleteWarning')}
        >
          {t('orderCenterShowSubtitleProfilePendingWarning')}
        </Alert>
      )
    }

    if (profileStatus === ProfileTaxStatusEnum.review) {
      return (
        <Alert
          leftIcon={<CloseIcon />}
          variant="danger"
          title={t('orderCenterShowSubtitleProfileIncompleteWarning')}
        >
          {t('orderCenterShowSubtitleProfileReviewWarning')}
        </Alert>
      )
    }

    return null
  }, [])

  return (
    <div className={styles.container}>
      <div>
        {saleOrder?.status !== 'cancelled' ? (
          <div className={styles.row}>
            <CardOrderDeliveyDate
              isLoading={!!isLoading}
              shipments={saleOrder?.shipments}
              saleOrderUuid={saleOrder?.uuid}
              saleOrderStatus={saleOrder?.status}
            />
            <CardOrderPaymentDate
              isLoading={!!isLoading}
              payments={saleOrder?.payments}
              saleOrderUuid={saleOrder?.uuid}
              saleOrderStatus={saleOrder?.status}
            />
          </div>
        ) : (
          <CardOrderCancellationReason
            isLoading={!!isLoading}
            cancellationReason={saleOrder?.cancellationReason}
          />
        )}

        <CardOrderStatuses isLoading={isLoading} saleOrder={saleOrder} />

        <CardOrderInformation
          isLoading={isLoading}
          createdAt={saleOrder?.createdAt}
          kam={saleOrder?.keyAccountManager?.email}
          invoiceFolio={saleOrder?.invoices[0]?.invoiceId}
          quotation={saleOrder?.quotation}
          uuid={saleOrder?.uuid || ''}
          disableNotifications={saleOrder?.disableNotifications}
        />
      </div>
      <div>
        <CardOrderSummaryValues
          discount={saleOrder?.discount ? +saleOrder.discount : 0}
          isLoading={!!isLoading}
          iva={saleOrder?.taxPrice ? +saleOrder?.taxPrice : 0}
          subTotal={
            saleOrder?.totalProductPrice ? +saleOrder?.totalProductPrice : 0
          }
          total={saleOrder?.totalOrderPrice ? +saleOrder?.totalOrderPrice : 0}
          totalFreight={
            saleOrder?.totalFreightPrice ? +saleOrder?.totalFreightPrice : 0
          }
          paymentForm={saleOrder?.payments[0]?.form as string}
          dollarExchangeRate={saleOrder?.dollarExchangeRate}
          paymentMethod={saleOrder?.payments[0]?.method as string}
        />

        {ProfileWarning}

        <CardOrderProfile
          isLoading={isLoading}
          buyer={saleOrder?.buyer}
          saleOrderUuid={saleOrder?.uuid}
        />
      </div>
    </div>
  )
}
